import React, { useState, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import PersonalDetails from "features/Auth/v2/Signup/SurmountRecommendations/PersonalDetails";
import PhoneVerification from "features/Auth/v2/Signup/SurmountRecommendations/PhoneVerification";
import IntroQuestions from "features/Auth/v2/Signup/SurmountRecommendations/IntroQuestions";
import SurmountAccountTaxW9Agreement from "features/Auth/v2/Signup/SetupFunding/SurmountAccount/TaxW9Agreement";
import { IconClose, PrismCoinIcon, AllSetICon } from "assets/icons";
import {
  CreateSurmountAccountModalContainer,
  ModalHeader,
  ModalTitle,
  TabsContainer,
  Tab,
  CloseButton,
  ProcessingScreenContainer,
  ProcessingIconWrapper,
  ProcessingTitle,
  ProcessingDescription,
  ProcessingButtonWrapper,
  AllSetScreenContainer,
  AllSetIconWrapper,
  AllSetTitle,
  AllSetDescription,
  AllSetButtonWrapper,
} from "./CreateSurmountAccountModal.styles";
import { useNavigationInterceptor } from "shared/hooks/useNavigationInterceptor";
import { useNavigation } from "utils/navigate";

const STEPS = {
  PERSONAL_DETAILS: 1,
  INVESTMENT_GOALS: 2,
  REVIEW_SUBMIT: 3,
  PROCESSING: 4,
  ALL_SET: 5,
};

const PERSONAL_DETAILS_SUB_STEPS = {
  FORM: "form",
  PHONE_VERIFICATION: "phone_verification",
};

// Route keys as enum-like constants for type safety and reusability
export const ROUTE_KEYS = {
  PHONE_VERIFICATION: "PHONE_VERIFICATION",
  PERSONAL_DETAILS: "PERSONAL_DETAILS",
  INTRO_QUESTIONS: "INTRO_QUESTIONS",
  TAX_W9: "TAX_W9",
  FUND_ACCOUNT: "FUND_ACCOUNT",
};

// Route patterns for navigation interception
// Using RegExp patterns for more precise and robust matching
// Patterns use .* to allow query params and trailing slashes for flexibility
// Centralized here to avoid tight coupling and make maintenance easier
const ROUTE_PATTERNS = {
  [ROUTE_KEYS.PHONE_VERIFICATION]: [
    /phone-verification.*$/,
    /\/signup\/surmount-recommendations\/phone-verification.*$/,
  ],
  [ROUTE_KEYS.PERSONAL_DETAILS]: [
    /personal-details.*$/,
    /funding-method.*$/,
    /\/signup\/surmount-recommendations\/personal-details.*$/,
    /\/signup\/setup-funding\/surmount-account\/personal-details.*$/,
  ],
  [ROUTE_KEYS.INTRO_QUESTIONS]: [
    /intro-questions.*$/,
    /\/signup\/intro-questions.*$/,
    /\/signup\/surmount-recommendations\/intro.*$/,
    /surmount-recommendations\/intro.*$/,
  ],
  [ROUTE_KEYS.TAX_W9]: [
    /tax-w9(-agreement)?.*$/,
    /\/signup\/surmount-account\/tax-w9-agreement.*$/,
    /\/signup\/setup-funding\/surmount-account\/tax-w9-agreement.*$/,
  ],
  [ROUTE_KEYS.FUND_ACCOUNT]: [/\/signup\/fund-your-account.*$/],
};

// Wrapper component for PersonalDetails that intercepts navigation
const PersonalDetailsWrapper = ({ onSuccess, onBack }) => {
  useNavigationInterceptor({
    interceptPatterns: ROUTE_PATTERNS[ROUTE_KEYS.PHONE_VERIFICATION],
    onIntercept: onSuccess,
    enabled: !!onSuccess,
  });

  // Intercept funding-method navigation (used by "Go Back" button)
  useNavigationInterceptor({
    interceptPatterns: [
      /funding-method.*$/,
      /\/signup\/.*choose-funding-method.*$/,
      /\/signup\/setup-funding\/.*choose-funding-method.*$/,
    ],
    onIntercept: () => {
      if (onBack) {
        onBack();
      }
    },
    enabled: !!onBack,
  });

  return <PersonalDetails />;
};

PersonalDetailsWrapper.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  onBack: PropTypes.func,
};

// Wrapper component for PhoneVerification that intercepts navigation
const PhoneVerificationWrapper = ({ onBack, onSuccess }) => {
  const successTriggered = useRef(false);

  useNavigationInterceptor({
    interceptPatterns: ROUTE_PATTERNS[ROUTE_KEYS.PERSONAL_DETAILS],
    onIntercept: () => {
      onBack();
    },
    enabled: !!onBack,
  });

  useNavigationInterceptor({
    interceptPatterns: ROUTE_PATTERNS[ROUTE_KEYS.INTRO_QUESTIONS],
    onIntercept: () => {
      if (!successTriggered.current && onSuccess) {
        successTriggered.current = true;
        onSuccess();
      }
    },
    enabled: !!onSuccess,
  });

  return <PhoneVerification />;
};

PhoneVerificationWrapper.propTypes = {
  onBack: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

// Wrapper component for IntroQuestions that intercepts navigation
const IntroQuestionsWrapper = ({ onBack, onSuccess }) => {
  const successTriggered = useRef(false);

  useNavigationInterceptor({
    interceptPatterns: [
      ...ROUTE_PATTERNS[ROUTE_KEYS.PHONE_VERIFICATION],
      ...ROUTE_PATTERNS[ROUTE_KEYS.PERSONAL_DETAILS],
    ],
    onIntercept: () => {
      onBack();
    },
    enabled: !!onBack,
  });

  useNavigationInterceptor({
    interceptPatterns: ROUTE_PATTERNS[ROUTE_KEYS.TAX_W9],
    onIntercept: () => {
      if (!successTriggered.current && onSuccess) {
        successTriggered.current = true;
        onSuccess();
      }
    },
    enabled: !!onSuccess,
  });

  return <IntroQuestions />;
};

IntroQuestionsWrapper.propTypes = {
  onBack: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

// Wrapper component for TaxW9Agreement that intercepts navigation
const TaxW9AgreementWrapper = ({ onBack, onSuccess, onModalClose }) => {
  const successTriggered = useRef(false);

  useNavigationInterceptor({
    interceptPatterns: ROUTE_PATTERNS[ROUTE_KEYS.INTRO_QUESTIONS],
    onIntercept: () => {
      onBack();
    },
    enabled: !!onBack,
  });

  useNavigationInterceptor({
    interceptPatterns: ROUTE_PATTERNS[ROUTE_KEYS.FUND_ACCOUNT],
    onIntercept: () => {
      if (!successTriggered.current) {
        successTriggered.current = true;
        if (onSuccess) {
          onSuccess();
        }
        // Close modal after a short delay to allow navigation
        setTimeout(() => {
          if (onModalClose) {
            onModalClose();
          }
        }, 100);
      }
    },
    enabled: !!onSuccess || !!onModalClose,
  });

  return <SurmountAccountTaxW9Agreement onSuccess={onSuccess} />;
};

TaxW9AgreementWrapper.propTypes = {
  onBack: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  onModalClose: PropTypes.func.isRequired,
};

export const CreateSurmountAccountModal = ({ show, onHide }) => {
  const { navigateToDashboard } = useNavigation();
  const [activeStep, setActiveStep] = useState(STEPS.PERSONAL_DETAILS);
  const [personalDetailsSubStep, setPersonalDetailsSubStep] = useState(
    PERSONAL_DETAILS_SUB_STEPS.FORM
  );
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [isInvestmentGoalsComplete, setIsInvestmentGoalsComplete] =
    useState(false);

  const clearValues = () => {
    setActiveStep(STEPS.PERSONAL_DETAILS);
    setPersonalDetailsSubStep(PERSONAL_DETAILS_SUB_STEPS.FORM);
    setIsPhoneVerified(false);
    setIsInvestmentGoalsComplete(false);
  };

  const handleModalClose = () => {
    clearValues();
    onHide();
  };

  const handlePersonalDetailsSuccess = () => {
    setPersonalDetailsSubStep(PERSONAL_DETAILS_SUB_STEPS.PHONE_VERIFICATION);
  };

  const handlePersonalDetailsBack = () => {
    handleModalClose();
  };

  const handlePhoneVerificationBack = () => {
    setPersonalDetailsSubStep(PERSONAL_DETAILS_SUB_STEPS.FORM);
  };

  const handlePhoneVerificationSuccess = () => {
    setIsPhoneVerified(true);
    setActiveStep(STEPS.INVESTMENT_GOALS);
  };

  const handleInvestmentGoalsBack = () => {
    setActiveStep(STEPS.PERSONAL_DETAILS);
    setPersonalDetailsSubStep(PERSONAL_DETAILS_SUB_STEPS.PHONE_VERIFICATION);
  };

  const handleInvestmentGoalsSuccess = () => {
    setIsInvestmentGoalsComplete(true);
    setActiveStep(STEPS.REVIEW_SUBMIT);
  };

  const handleReviewSubmitBack = () => {
    setActiveStep(STEPS.INVESTMENT_GOALS);
  };

  const handleReviewSubmitSuccess = () => {
    setActiveStep(STEPS.PROCESSING);
  };

  const handleProcessingContinue = () => {
    setActiveStep(STEPS.ALL_SET);
  };

  const handleAllSetContinue = () => {
    clearValues();
    onHide();
    navigateToDashboard();
  };

  const tabs = useMemo(
    () => [
      { id: 1, label: "1. Personal details", step: STEPS.PERSONAL_DETAILS },
      {
        id: 2,
        label: "2. Investment goals",
        step: STEPS.INVESTMENT_GOALS,
        disabled: !isPhoneVerified,
      },
      {
        id: 3,
        label: "3. Review and submit",
        step: STEPS.REVIEW_SUBMIT,
        disabled: !isInvestmentGoalsComplete,
      },
    ],
    [isPhoneVerified, isInvestmentGoalsComplete]
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case STEPS.PERSONAL_DETAILS:
        if (
          personalDetailsSubStep ===
          PERSONAL_DETAILS_SUB_STEPS.PHONE_VERIFICATION
        ) {
          return (
            <PhoneVerificationWrapper
              onBack={handlePhoneVerificationBack}
              onSuccess={handlePhoneVerificationSuccess}
            />
          );
        }
        return (
          <PersonalDetailsWrapper
            onSuccess={handlePersonalDetailsSuccess}
            onBack={handlePersonalDetailsBack}
          />
        );
      case STEPS.INVESTMENT_GOALS:
        return (
          <IntroQuestionsWrapper
            onBack={handleInvestmentGoalsBack}
            onSuccess={handleInvestmentGoalsSuccess}
          />
        );
      case STEPS.REVIEW_SUBMIT:
        return (
          <TaxW9AgreementWrapper
            onBack={handleReviewSubmitBack}
            onSuccess={handleReviewSubmitSuccess}
            onModalClose={onHide}
          />
        );
      case STEPS.PROCESSING:
        return (
          <ProcessingScreenContainer>
            <div className="container-processing">
              <ProcessingIconWrapper>
                <PrismCoinIcon />
              </ProcessingIconWrapper>
              <ProcessingTitle>Processing Application...</ProcessingTitle>
              <ProcessingDescription>
                We&apos;re processing your application. This might take 1-2
                days.
              </ProcessingDescription>
              <ProcessingButtonWrapper>
                <button onClick={handleProcessingContinue}>
                  Continue to Dashboard
                </button>
              </ProcessingButtonWrapper>
            </div>
          </ProcessingScreenContainer>
        );
      case STEPS.ALL_SET:
        return (
          <AllSetScreenContainer>
            <div className="container-all-set">
              <AllSetIconWrapper>
                <AllSetICon />
              </AllSetIconWrapper>
              <AllSetTitle>You&apos;re all set</AllSetTitle>
              <AllSetDescription>
                We&apos;re processing your application. This might take a
                minute.
              </AllSetDescription>
              <AllSetButtonWrapper>
                <button onClick={handleAllSetContinue}>Continue</button>
              </AllSetButtonWrapper>
            </div>
          </AllSetScreenContainer>
        );
      default:
        return (
          <PersonalDetailsWrapper
            onSuccess={handlePersonalDetailsSuccess}
            onBack={handlePersonalDetailsBack}
          />
        );
    }
  };

  return (
    <CreateSurmountAccountModalContainer
      show={show}
      onHide={handleModalClose}
      size="xl"
      showClose={false}
    >
      {activeStep !== STEPS.PROCESSING && activeStep !== STEPS.ALL_SET && (
        <ModalHeader>
          <div>
            <ModalTitle>Apply for Surmount account</ModalTitle>
            <TabsContainer>
              {tabs.map((tab) => (
                <Tab
                  key={tab.id}
                  $active={activeStep === tab.step}
                  disabled={tab.disabled}
                  onClick={() =>
                    !tab.disabled && tab.step && setActiveStep(tab.step)
                  }
                >
                  {tab.label}
                </Tab>
              ))}
            </TabsContainer>
          </div>
          <CloseButton onClick={handleModalClose} aria-label="Close modal">
            <IconClose />
          </CloseButton>
        </ModalHeader>
      )}
      <div
        className="modal-content-wrapper"
        data-step={
          activeStep === STEPS.PERSONAL_DETAILS
            ? personalDetailsSubStep ===
              PERSONAL_DETAILS_SUB_STEPS.PHONE_VERIFICATION
              ? "PHONE_VERIFICATION"
              : "PERSONAL_DETAILS"
            : activeStep === STEPS.INVESTMENT_GOALS
            ? "INVESTMENT_GOALS"
            : activeStep === STEPS.REVIEW_SUBMIT
            ? "REVIEW_SUBMIT"
            : null
        }
      >
        {renderStepContent()}
      </div>
    </CreateSurmountAccountModalContainer>
  );
};

CreateSurmountAccountModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
};

CreateSurmountAccountModal.displayName = "CreateSurmountAccountModal";
